import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react'
import { logEvent } from '../analytics/analytics'
import { ensureAnonId } from '../helpers/anon-id'
import { getLocationLocalStorage } from '../helpers/location-helper'
import {
  clearPlannerDraft,
  readPlannerDraft,
  writePlannerDraft,
} from '../helpers/planner-draft'
import {
  addSessionCandidate,
  createGroupSession,
  fetchSessionCandidates,
  removeSessionCandidate,
} from '../services/groupSessionService'
import { errorLog } from '../utils/log-helper'

/**
 * Planner mode — the low-friction "value first, configuration later" planner flow.
 *
 * The old flow demanded an occasion, a REQUIRED location, a date, a cuisine and a party size before
 * a planner could exist at all: 658 users reached that form and 25 created (3.8%). This context
 * inverts it. A planner is created from nothing but the device's anon id (the backend has always
 * only required that — the friction was entirely in the form), the user keeps browsing and taps
 * restaurants onto it, and configuration is asked for later, only when it actually buys something.
 *
 * ARCHITECTURE: a "draft" is a REAL backend group session, not a client-only object. That choice is
 * what preserves the whole existing stack unchanged — share_token, the 30-day TTL, guest-eater
 * provisioning, PARTY_CAP, joining, voting, the tally, and guest->registered claim-on-signup all
 * work on a draft the moment it exists, and existing share links keep resolving exactly as before.
 * "Draft" is purely a client-side state word for "started with zero configuration, not yet shared".
 *
 * IDEMPOTENCY comes from three layers, because a planner minted twice is the worst failure here:
 *   1. the draft pointer in localStorage, so a reload/relaunch reuses the same planner;
 *   2. an in-flight promise guard (`creationRef`), so two fast taps share one create call;
 *   3. the backend's (group_id, restaurant_alias) unique key, so a repeat add is a no-op.
 *
 * Outside planner mode nothing here runs and ordinary browsing is untouched.
 */

/** A restaurant being added to a planner. Only the alias is load-bearing; the rest is analytics. */
export interface PlannerRestaurantInput {
  alias?: string
  restaurantId?: string
  name?: string
  latitude?: number
  longitude?: number
  recommendationId?: string
  searchId?: string
  rankPosition?: number
  /** The browsing surface the pick was made from (home, search, dine_session, ...). */
  sourceSurface?: string
}

interface PlannerModeContextType {
  /** True while the user is browsing in planner-selection mode. */
  active: boolean
  /** The active planner's share token, or null when there is none. */
  shareToken: string | null
  /** Aliases currently on the planner — the source of truth for a card's selected state. */
  candidateAliases: string[]
  /** Shortlist size; what the persistent planner bar shows. */
  candidateCount: number
  /** True while a create/add/remove round-trip is in flight (disables the card action). */
  busy: boolean
  /** True when this device has an unfinished draft it can resume. */
  hasDraft: boolean
  /** Is this restaurant already on the planner? */
  isOnPlanner: (alias?: string) => boolean
  /**
   * Enter planner-selection mode, creating a draft planner if one isn't already active. Optionally
   * seeds it with the restaurant that triggered it. Safe to call repeatedly — never creates a
   * second planner.
   */
  startPlanner: (
    entryPoint: string,
    seed?: PlannerRestaurantInput,
  ) => Promise<string | null>
  /** Add or remove a restaurant, creating the planner first if needed. The card action calls this. */
  togglePlannerRestaurant: (
    restaurant: PlannerRestaurantInput,
  ) => Promise<void>
  /** Resume the persisted draft and re-enter planner mode. */
  resumePlanner: (entryPoint?: string) => Promise<string | null>
  /** Record that the planner link was shared; graduates the draft out of "abandonable". */
  markPlannerShared: (method: 'native' | 'copy_link' | 'unknown') => void
  /** Leave planner-selection mode. Emits session_abandoned when the planner was never shared. */
  exitPlannerMode: (reason?: string) => void
}

const PlannerModeContext = createContext<PlannerModeContextType | undefined>(
  undefined,
)

export const PlannerModeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [active, setActive] = useState(false)
  const [shareToken, setShareToken] = useState<string | null>(null)
  const [candidateAliases, setCandidateAliases] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [hasDraft, setHasDraft] = useState(false)

  // In-flight create guard: two taps in the same tick must share ONE create call, or the user ends
  // up with two planners and the second silently swallows the first restaurant.
  const creationRef = useRef<Promise<string | null> | null>(null)
  const startedAtRef = useRef<number>(Date.now())
  const sharedRef = useRef(false)

  // Surface an existing unfinished draft (e.g. after a reload) so the Planner landing can offer to
  // resume it. Does NOT re-enter planner mode on its own — resuming is always a user action.
  useEffect(() => {
    setHasDraft(readPlannerDraft() !== null)
  }, [])

  const persist = useCallback((token: string, shared: boolean): void => {
    writePlannerDraft({
      shareToken: token,
      startedAt: startedAtRef.current,
      shared,
    })
    setHasDraft(true)
  }, [])

  /**
   * Create the draft planner. Location is NOT required: we use whatever usable context exists —
   * the seeding restaurant's own coordinates first (the most relevant signal: this is where the
   * user is already looking), then the device's last-known location. When neither exists the
   * planner is created with no location at all, which the backend has always accepted.
   */
  const createDraft = useCallback(
    async (
      entryPoint: string,
      seed?: PlannerRestaurantInput,
    ): Promise<string | null> => {
      const anonId = await ensureAnonId()
      const lastKnown = getLocationLocalStorage()
      const seedHasCoords =
        typeof seed?.latitude === 'number' && typeof seed?.longitude === 'number'
      const lastKnownHasCoords =
        typeof lastKnown?.latitude === 'number' &&
        typeof lastKnown?.longitude === 'number'

      let latitude: number | undefined
      let longitude: number | undefined
      let locationSource: 'restaurant' | 'last_known' | 'none' = 'none'
      if (seedHasCoords) {
        latitude = seed?.latitude
        longitude = seed?.longitude
        locationSource = 'restaurant'
      } else if (lastKnownHasCoords) {
        latitude = lastKnown.latitude
        longitude = lastKnown.longitude
        locationSource = 'last_known'
      }

      const session = await createGroupSession({
        anonId,
        // No occasion is asked for up front; the planner is renameable later.
        name: 'Dinner',
        latitude,
        longitude,
        origin: 'web',
      })
      if (!session.valid || !session.shareToken) {
        return null
      }
      startedAtRef.current = Date.now()
      sharedRef.current = false
      persist(session.shareToken, false)

      logEvent('session_draft_started', {
        share_token: session.shareToken,
        entry_point: entryPoint,
        location_source: locationSource,
        screen_name: 'planner_mode',
      })
      // The draft IS a session — keep the canonical creation event firing so the existing
      // create -> share -> open -> join funnel stays continuous across both experiment arms.
      logEvent('session_created', {
        share_token: session.shareToken,
        party_cap: session.partyCap,
        session_origin: 'web',
        screen_name: 'planner_mode',
      })
      return session.shareToken
    },
    [persist],
  )

  /** Resolve the planner to act on, creating one at most once across concurrent callers. */
  const ensurePlanner = useCallback(
    async (
      entryPoint: string,
      seed?: PlannerRestaurantInput,
    ): Promise<string | null> => {
      if (shareToken) {
        return shareToken
      }
      const existing = readPlannerDraft()
      if (existing) {
        startedAtRef.current = existing.startedAt
        sharedRef.current = existing.shared
        setShareToken(existing.shareToken)
        return existing.shareToken
      }
      if (creationRef.current) {
        return await creationRef.current
      }
      const pending = createDraft(entryPoint, seed).finally(() => {
        creationRef.current = null
      })
      creationRef.current = pending
      const token = await pending
      if (token) {
        setShareToken(token)
      }
      return token
    },
    [shareToken, createDraft],
  )

  const startPlanner = useCallback(
    async (
      entryPoint: string,
      seed?: PlannerRestaurantInput,
    ): Promise<string | null> => {
      setBusy(true)
      try {
        const token = await ensurePlanner(entryPoint, seed)
        if (token) {
          setActive(true)
          const current = await fetchSessionCandidates(token)
          setCandidateAliases(current.candidates.map((c) => c.restaurantAlias))
        }
        return token
      } catch (e) {
        errorLog('[PlannerMode] startPlanner failed', e)
        return null
      } finally {
        setBusy(false)
      }
    },
    [ensurePlanner],
  )

  const resumePlanner = useCallback(
    async (entryPoint = 'planner_landing'): Promise<string | null> => {
      const existing = readPlannerDraft()
      if (!existing) {
        return null
      }
      setBusy(true)
      try {
        startedAtRef.current = existing.startedAt
        sharedRef.current = existing.shared
        setShareToken(existing.shareToken)
        const current = await fetchSessionCandidates(existing.shareToken)
        // A draft whose backend session has expired or been deleted is not resumable — drop the
        // stale pointer rather than trapping the user in a planner that no longer exists.
        if (!current.valid) {
          clearPlannerDraft()
          setHasDraft(false)
          setShareToken(null)
          return null
        }
        setCandidateAliases(current.candidates.map((c) => c.restaurantAlias))
        setActive(true)
        logEvent('session_resumed', {
          share_token: existing.shareToken,
          candidate_count: current.candidateCount,
          entry_point: entryPoint,
          screen_name: 'planner_mode',
        })
        return existing.shareToken
      } catch (e) {
        errorLog('[PlannerMode] resumePlanner failed', e)
        return null
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  const togglePlannerRestaurant = useCallback(
    async (restaurant: PlannerRestaurantInput): Promise<void> => {
      const alias = restaurant.alias
      if (!alias) {
        return
      }
      setBusy(true)
      try {
        const token = await ensurePlanner('restaurant_card', restaurant)
        if (!token) {
          return
        }
        setActive(true)
        const anonId = await ensureAnonId()
        const alreadyOn = candidateAliases.includes(alias)
        const result = alreadyOn
          ? await removeSessionCandidate({
              shareToken: token,
              anonId,
              restaurantAlias: alias,
            })
          : await addSessionCandidate({
              shareToken: token,
              anonId,
              restaurantAlias: alias,
              sourceSurface: restaurant.sourceSurface,
            })
        if (!result.valid) {
          return
        }
        setCandidateAliases(result.candidates.map((c) => c.restaurantAlias))
        logEvent(
          alreadyOn ? 'session_restaurant_removed' : 'session_restaurant_added',
          {
            share_token: token,
            restaurant_id: restaurant.restaurantId ?? '',
            restaurant_alias: alias,
            candidate_count: result.candidateCount,
            source_surface: restaurant.sourceSurface,
            recommendation_id: restaurant.recommendationId,
            search_id: restaurant.searchId,
            rank_position: restaurant.rankPosition,
            screen_name: 'planner_mode',
          },
        )
      } catch (e) {
        errorLog('[PlannerMode] togglePlannerRestaurant failed', e)
      } finally {
        setBusy(false)
      }
    },
    [ensurePlanner, candidateAliases],
  )

  const markPlannerShared = useCallback(
    (method: 'native' | 'copy_link' | 'unknown'): void => {
      if (!shareToken) {
        return
      }
      sharedRef.current = true
      persist(shareToken, true)
      logEvent('session_shared', {
        share_token: shareToken,
        share_method: method,
        candidate_count: candidateAliases.length,
        screen_name: 'planner_mode',
      })
    },
    [shareToken, candidateAliases.length, persist],
  )

  const exitPlannerMode = useCallback(
    (reason = 'user_exit'): void => {
      // A planner exited without ever being shared is the direct successor to the old form's
      // abandonment — emit it explicitly rather than inferring it from an absence.
      if (shareToken && !sharedRef.current) {
        logEvent('session_abandoned', {
          share_token: shareToken,
          candidate_count: candidateAliases.length,
          duration_seconds: Math.round(
            (Date.now() - startedAtRef.current) / 1000,
          ),
          screen_name: 'planner_mode',
          source: reason === 'user_exit' ? 'in_app' : undefined,
        })
      }
      setActive(false)
      setCandidateAliases([])
      setShareToken(null)
      // The pointer is dropped only once shared (the planner has graduated); an unshared planner
      // stays resumable from the Planner landing so a back-out isn't a dead end.
      if (sharedRef.current) {
        clearPlannerDraft()
        setHasDraft(false)
      }
    },
    [shareToken, candidateAliases.length],
  )

  const isOnPlanner = useCallback(
    (alias?: string): boolean =>
      !!alias && candidateAliases.includes(alias),
    [candidateAliases],
  )

  const value = useMemo<PlannerModeContextType>(
    () => ({
      active,
      shareToken,
      candidateAliases,
      candidateCount: candidateAliases.length,
      busy,
      hasDraft,
      isOnPlanner,
      startPlanner,
      togglePlannerRestaurant,
      resumePlanner,
      markPlannerShared,
      exitPlannerMode,
    }),
    [
      active,
      shareToken,
      candidateAliases,
      busy,
      hasDraft,
      isOnPlanner,
      startPlanner,
      togglePlannerRestaurant,
      resumePlanner,
      markPlannerShared,
      exitPlannerMode,
    ],
  )

  return (
    <PlannerModeContext.Provider value={value}>
      {children}
    </PlannerModeContext.Provider>
  )
}

/**
 * Planner-mode accessor. Returns undefined outside the provider (rather than throwing) so shared
 * components like RestaurantCard — which render in tests, storybook-style harnesses and surfaces
 * mounted outside the provider — degrade to "no planner action" instead of crashing.
 */
export const usePlannerMode = (): PlannerModeContextType | undefined =>
  useContext(PlannerModeContext)
